import { ref, onUnmounted } from 'vue'

/**
 * Composable for tracking image processing status via MQTT
 * Uses Server-Sent Events to receive real-time updates
 *
 * @param {Object} options - Configuration options
 * @param {string} options.publicId - The Cloudinary publicId to monitor
 * @param {string} options.filename - The filename to monitor (alternative to publicId)
 * @returns {Object} Status tracking state and methods
 */
export function useImageStatus(options = {}) {
  const { publicId, filename } = options
  // Processing states
  const isProcessing = ref(false)
  const isReady = ref(false)
  const currentStage = ref(0)
  const statusMessage = ref('')
  const progress = ref(0)
  const error = ref(null)

  // EventSource for SSE
  let eventSource = null

  /**
   * Stage information mapping
   */
  const stages = {
    1: { key: 'uploaded', label: 'Image is uploaded' },
    2: { key: 'processing', label: 'Image is being processed' },
    3: { key: 'branding', label: 'Adding brand watermark' },
    4: { key: 'finished', label: 'Image is ready' }
  }

  /**
   * Start monitoring image processing status
   */
  const startMonitoring = () => {
    if ((!publicId && !filename) || eventSource) return
    isProcessing.value = true
    error.value = null
    currentStage.value = 0

    try {
      // Build API URL with appropriate parameter
      if (import.meta.env.DEV) {
        console.log('Starting function to monitor image status for', publicId || filename)
      }
      let apiUrl = '/api/image-status?'

      if (filename) {
        // Use filename directly
        apiUrl += `filename=${encodeURIComponent(filename)}`
      } else if (publicId) {
        // Use publicId (will be transformed server-side)
        apiUrl += `publicId=${encodeURIComponent(publicId)}`
      }
      if (import.meta.env.DEV) {
        console.log('API URLs:', apiUrl)
      }
      // Connect to SSE endpoint
      eventSource = new EventSource(apiUrl)

      eventSource.onopen = () => {
        if (import.meta.env.DEV) {
          console.log('SSE connection opened for image status')
        }
      }

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (import.meta.env.DEV) {
            console.log('Status update received:', data)
          }

          // Handle different event types
          if (data.type === 'connected') {
            if (import.meta.env.DEV) {
              console.log('Connected to MQTT topic:', data.topic)
            }
            statusMessage.value = 'Monitoring image processing...'
          } else if (data.type === 'status') {
            // Update processing state
            currentStage.value = data.stage || 0
            progress.value = data.progress || 0
            statusMessage.value = data.message || stages[data.stage]?.label || ''

            // Check if finished
            if (data.status === 'finished' || data.stage === 4) {
              isProcessing.value = false
              stopMonitoring()
              // Small delay to allow CDN propagation before the component re-fetches
              setTimeout(() => {
                isReady.value = true
              }, 1500)
            }
          } else if (data.type === 'timeout') {
            if (import.meta.env.DEV) {
              console.warn('SSE connection timeout')
            }
            statusMessage.value = 'Connection timeout. Image may still be processing.'
            isProcessing.value = false
            stopMonitoring()
          } else if (data.error) {
            error.value = data.error
            isProcessing.value = false
            stopMonitoring()
          }

        } catch (err) {
          console.error('Error parsing SSE data:', err)
        }
      }

      eventSource.onerror = (err) => {
        console.error('SSE error:', err)

        // Check if this is a MIME type error (API not available in dev mode)
        if (err.target && err.target.readyState === EventSource.CLOSED) {
          if (import.meta.env.DEV) {
            console.warn('SSE connection failed. API endpoints require Vercel dev or production deployment.')
            console.warn('To enable MQTT monitoring locally, use: vercel dev')
          }
          error.value = null // Don't show error to user, just disable monitoring
        } else {
          error.value = 'Connection error. Unable to monitor processing status.'
        }

        isProcessing.value = false
        stopMonitoring()
      }

    } catch (err) {
      console.error('Failed to start monitoring:', err)
      error.value = err.message
      isProcessing.value = false
    }
  }

  /**
   * Stop monitoring
   */
  const stopMonitoring = () => {
    if (eventSource) {
      eventSource.close()
      eventSource = null
    }
    isProcessing.value = false
  }

  /**
   * Get stage information
   */
  const getStageInfo = (stage) => {
    return stages[stage] || { key: 'unknown', label: 'Unknown stage' }
  }

  // Cleanup on component unmount
  onUnmounted(() => {
    stopMonitoring()
  })

  return {
    // State
    isProcessing,
    isReady,
    currentStage,
    statusMessage,
    progress,
    error,

    // Methods
    startMonitoring,
    stopMonitoring,
    getStageInfo,

    // Constants
    stages
  }
}
